import {
  type ExcludeStrict,
  type ExtractStrict,
  ReactUtils,
} from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps } from 'react'

import { Tooltip } from '../../feedback/tooltip/Tooltip'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { Icon } from '../../structure/icon/Icon'
import { ButtonLoadingContent } from './ButtonLoadingContent'
import { buttonStyles } from './buttonStyles'

type IconName = ComponentProps<typeof Icon>['name']
type ButtonStylesArgs = Parameters<typeof buttonStyles>[0]

type Props = PropsWithSafeExtra<
  'button',
  'type' | 'disabled' | 'onClick' | 'children' | 'className' | 'form',
  {
    isLoading?: boolean
    isFullWidth?: ButtonStylesArgs['isFullWidth']
    isDanger?: ButtonStylesArgs['isDanger']
    tip?: ComponentProps<typeof Tooltip>['tip']
  } & (
    | {
        endIcon?: IconName
        startIcon?: IconName
        variant?: ExcludeStrict<ButtonStylesArgs['variant'], 'text'>
      }
    | {
        endIcon?: never
        startIcon?: never
        variant?: ExtractStrict<ButtonStylesArgs['variant'], 'text'>
      }
  )
>

/**
 * Button with loading state, icons and various styles.
 *
 * Also available as buttonStyles if you need to apply only the styles to another component.
 */
export const Button = ({
  isFullWidth = false,
  disabled = false,
  children,
  type = 'button',
  variant = 'secondary',
  endIcon,
  startIcon,
  onClick,
  className,
  isLoading = false,
  isDanger = false,
  tip,
  extraProps = {},
  ref,
  ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
}: Props) => {
  const isDisabled = disabled || isLoading
  const commonIconProps = {
    color: 'unset' as const,
    title: '', // deliberately empty since the icon is decorative only
  }
  const hasTooltip = !!tip

  return (
    <ReactUtils.ConditionalWrap
      condition={hasTooltip}
      wrap={(children) => (
        <Tooltip ref={ref} tip={tip}>
          {/**
           * Tooltips for a disabled button don't work since
           * the button doesn't receive pointer events, so we
           * wrap the children in a span to ensure the tooltip
           * works even when the button is disabled.
           */}
          {isDisabled ? (
            <span
              tabIndex={0}
              className={isFullWidth ? 'flex w-full' : 'inline-flex'} // Aligned with the button's display style
            >
              {children}
            </span>
          ) : (
            children
          )}
        </Tooltip>
      )}
    >
      <button
        {...safeRestProps(extraProps, restProps)}
        ref={hasTooltip ? undefined : ref}
        type={type}
        className={cx(
          buttonStyles({
            variant,
            disabled: isDisabled,
            isDanger,
            isFullWidth,
          }),
          className,
        )}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : onClick}
      >
        <ButtonLoadingContent
          isLoading={
            variant !== 'text' && // text doesn't have icons nor loading spinners, it's just disabled when loading
            isLoading
          }
        >
          {startIcon && (
            <Icon
              name={startIcon}
              className="-my-1 me-1 -ms-0.5"
              {...commonIconProps}
            />
          )}
          {children}
          {endIcon && (
            <Icon
              name={endIcon}
              className="-my-1 ms-1 -me-0.5"
              {...commonIconProps}
            />
          )}
        </ButtonLoadingContent>
      </button>
    </ReactUtils.ConditionalWrap>
  )
}
